import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { BatteryFull, Cpu, ShieldCheck, Recycle, Info } from "@phosphor-icons/react";
import PageShell from "@/components/dashboard/PageShell";
import { api, endpoints } from "@/lib/api";
import { PAGES, DASHBOARD } from "@/constants/testIds";

const Metric = ({ icon: Icon, color, label, value, suffix, decimals = 0 }) => (
    <div className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-6">
        <div className="flex items-center gap-2" style={{ color }}>
            <Icon size={16} weight="fill" />
            <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em]">
                {label}
            </span>
        </div>
        <div className="mt-4 font-mono-metric text-4xl font-bold text-white sm:text-5xl md:text-6xl">
            <CountUp end={value} separator="," duration={2} decimals={decimals} />
            {suffix && (
                <span className="ml-2 text-base font-normal text-zinc-500">{suffix}</span>
            )}
        </div>
    </div>
);

const Battery = () => {
    const [data, setData] = useState(null);
    const [community, setCommunity] = useState(null);

    useEffect(() => {
        let ignore = false;
        Promise.all([
            api.get(endpoints.batteryImpact()),
            api.get(endpoints.communityBattery()),
        ])
            .then(([b, c]) => {
                if (ignore) return;
                setData(b.data);
                setCommunity(c.data);
            })
            .catch(() => {});
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <PageShell testId={PAGES.battery}>
            <div className="flex flex-col gap-6 sm:gap-8">
                <header className="rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10">
                    <span className="eviq-chip">Battery Legacy · Deep dive</span>
                    <h1 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                        The battery you keep alive is the mine we never open.
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm text-zinc-400 lg:text-base">
                        EVIQ measures Battery Legacy in years of pack life extended and in the
                        critical minerals — lithium, nickel, cobalt — that stay in the ground because
                        of it. This page shows the full picture, plus the methodology behind every
                        number.
                    </p>
                </header>

                {data && (
                    <section
                        data-testid={DASHBOARD.battery}
                        className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10"
                    >
                        <div className="mb-6 flex flex-col gap-3 sm:mb-8 md:flex-row md:items-end md:justify-between">
                            <div>
                                <span className="eviq-chip">Signature EVIQ metric</span>
                                <h2 className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl">
                                    Community battery legacy, live.
                                </h2>
                            </div>
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-600">
                                {data.methodology_note}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Metric
                                icon={BatteryFull}
                                color="#a3e635"
                                label="Community battery life extended"
                                value={data.estimated_battery_life_extended_years}
                                suffix="years"
                            />
                            <Metric
                                icon={Cpu}
                                color="#8b5cf6"
                                label="Batteries kept in service longer"
                                value={data.estimated_batteries_kept_in_service}
                            />
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                            {[
                                { label: "Lithium", value: data.critical_minerals_conserved.lithium_kg, color: "#a3e635" },
                                { label: "Nickel", value: data.critical_minerals_conserved.nickel_kg, color: "#06b6d4" },
                                { label: "Cobalt", value: data.critical_minerals_conserved.cobalt_kg, color: "#f59e0b" },
                            ].map((m) => (
                                <div
                                    key={m.label}
                                    className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-6"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="h-2 w-2 rounded-full"
                                            style={{ background: m.color }}
                                        />
                                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                            {m.label} conserved
                                        </span>
                                    </div>
                                    <div className="mt-3 font-mono-metric text-3xl font-bold text-white">
                                        <CountUp end={m.value} separator="," decimals={0} duration={2} />
                                        <span className="ml-1 text-sm font-normal text-zinc-500">kg</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {community && (
                    <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                        <span className="eviq-chip">Mining deferred (tons)</span>
                        <h2 className="mt-4 font-editorial text-2xl text-white sm:text-3xl">
                            Every year not-yet-mined stays on the balance sheet.
                        </h2>
                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                            <Metric
                                icon={Recycle}
                                color="#a3e635"
                                label="Lithium not mined"
                                value={community.mining_deferred.lithium_tons}
                                suffix="tons"
                                decimals={1}
                            />
                            <Metric
                                icon={Recycle}
                                color="#06b6d4"
                                label="Nickel not mined"
                                value={community.mining_deferred.nickel_tons}
                                suffix="tons"
                                decimals={1}
                            />
                            <Metric
                                icon={Recycle}
                                color="#f59e0b"
                                label="Cobalt not mined"
                                value={community.mining_deferred.cobalt_tons}
                                suffix="tons"
                                decimals={1}
                            />
                        </div>
                    </section>
                )}

                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={16} weight="fill" color="#a3e635" />
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Methodology
                        </span>
                    </div>
                    <h2 className="mt-3 font-editorial text-2xl text-white sm:text-3xl">
                        How we count every kilogram.
                    </h2>
                    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        {[
                            {
                                title: "Pack composition",
                                body: "We use a weighted average of NMC, NCA, and LFP cell chemistries reflecting the current EVIQ member fleet. Each chemistry contributes a different mass of lithium, nickel, and cobalt per kWh.",
                            },
                            {
                                title: "Service-life extension",
                                body: "Batteries kept in service longer are counted only when a member's charging patterns cross a conservative threshold (State-of-Charge management, thermal preconditioning, and time-below-80%).",
                            },
                            {
                                title: "Conservative rounding",
                                body: "When in doubt, we round down. If a pack lasts one extra year, we count one year — never two. The full memo is published every January on the Community page.",
                            },
                        ].map((c) => (
                            <div
                                key={c.title}
                                className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-6"
                            >
                                <div className="flex items-center gap-2 text-zinc-400">
                                    <Info size={14} />
                                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em]">
                                        Note
                                    </span>
                                </div>
                                <h3 className="mt-3 font-editorial text-lg text-white">
                                    {c.title}
                                </h3>
                                <p className="mt-2 text-sm text-zinc-400">{c.body}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </PageShell>
    );
};

export default Battery;
